/**
 * Custom hook for streaming search via Server-Sent Events.
 *
 * Consumes the /v2/search/drugs/stream SSE endpoint and
 * progressively builds up DrugAggregatedResponseV2 as drug batches arrive.
 *
 * Results appear in the table as each reranker batch completes (~3 drugs at a time),
 * giving first results within 10-15s instead of waiting 80s for the full pipeline.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { auth } from '../config/firebase';
import { DEFAULT_MARKETING_CATEGORIES } from '../constants/filterConstants';
import { authApi } from '../services/authApi';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import type { DrugAggregatedResponseV2, DrugResultV2, DraftResultV2, IntentResponseV2, SearchMetadataV2 } from '../types';

// SSE streaming requires the Gen 2 Cloud Function (Gen 1 buffers entire response).
// VITE_STREAMING_API_URL should point to the Gen 2 apiStreamProxy Cloud Run URL.
const STREAMING_API_URL = import.meta.env['VITE_STREAMING_API_URL'] ||
  import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

interface StreamingSearchOptions {
  marketingCategories?: string[];
  /** Multi-region filter. When non-empty, sent to backend as ``regions``. */
  regions?: string[];
  /** Legacy single-region filter; ignored when ``regions`` is non-empty. */
  region?: string | undefined;
  drug_filter?: string;
  section_filter?: string[];
  /** When true, includes draft label corpus results in the response. */
  include_label_negotiations?: boolean;
  /** Draft hit scope: 'all' (draft content + comments, default) | 'comments_only'. */
  draft_comment_scope?: 'all' | 'comments_only';
  /** Search corpus. Omitted/'labels' -> request body unchanged (no new keys). */
  corpus?: 'labels' | 'ha_reviews';
  /** HA document types; sent only when ``corpus === 'ha_reviews'``. */
  review_doc_types?: string[];
  /** Bumped on every Ask so re-submitting unchanged inputs re-runs the stream. */
  submitGen?: number;
}

interface StreamingSearchState {
  /** Accumulated results — grows as drug batches arrive */
  data: DrugAggregatedResponseV2 | null;
  /** True while the SSE stream is active */
  isLoading: boolean;
  /** Error if the stream or connection failed */
  error: Error | null;
  /** Number of drugs received so far (updates live) */
  drugCount: number;
}

interface SSEEvent {
  type: string;
  data: string;
}

/**
 * Parse an SSE buffer into individual events.
 * Returns parsed events and the remaining unparsed buffer.
 */
function parseSSEBuffer(buffer: string): { events: SSEEvent[]; remaining: string } {
  const events: SSEEvent[] = [];
  const parts = buffer.split('\n\n');

  // Last part may be incomplete — keep it as remaining
  const remaining = parts.pop() || '';

  for (const part of parts) {
    if (!part.trim()) continue;

    let eventType = 'message';
    let eventData = '';

    for (const line of part.split('\n')) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        eventData = line.slice(6);
      }
    }

    if (eventData) {
      events.push({ type: eventType, data: eventData });
    }
  }

  return { events, remaining };
}

export function useStreamingSearch(
  query: string,
  options: StreamingSearchOptions = {},
  enabled: boolean = true,
): StreamingSearchState {
  const [data, setData] = useState<DrugAggregatedResponseV2 | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [drugCount, setDrugCount] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  // Stable reference for options to avoid unnecessary re-runs
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const requestKey = useMemo(() => {
    const canonicalList = (values?: string[]) =>
      values?.length ? [...new Set(values)].sort().join(',') : '';

    return JSON.stringify({
      marketingCategories: canonicalList(options.marketingCategories),
      regions: canonicalList(options.regions),
      region: options.regions?.length ? '' : (options.region ?? ''),
      drug_filter: options.drug_filter ?? '',
      section_filter: canonicalList(options.section_filter),
      include_label_negotiations: options.include_label_negotiations ?? false,
      draft_comment_scope: options.draft_comment_scope ?? 'all',
      corpus: options.corpus === 'ha_reviews' ? 'ha_reviews' : '',
      review_doc_types: options.corpus === 'ha_reviews' ? canonicalList(options.review_doc_types) : '',
      submitGen: options.submitGen ?? 0,
    });
  }, [
    options.marketingCategories,
    options.regions,
    options.region,
    options.drug_filter,
    options.section_filter,
    options.include_label_negotiations,
    options.draft_comment_scope,
    options.corpus,
    options.review_doc_types,
    options.submitGen,
  ]);

  const startStream = useCallback(async (searchQuery: string) => {
    // Abort any previous stream
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    setData(null);
    setDrugCount(0);

    // Recover persisted results when the live stream fails to deliver a
    // terminal `done` event (a transient proxy/transport cut). The backend
    // always persists results before completing — that's why the history view
    // is reliable — so we re-read them from the non-streaming endpoint (the
    // same source history uses) instead of falsely rendering "No results
    // found". Returns true if authoritative results were applied.
    const recoverViaFallback = async (): Promise<boolean> => {
      if (controller.signal.aborted) return false;
      try {
        const opts = optionsRef.current;
        const recovered = (await authApi.searchV2Drugs(searchQuery, {
          top_k: 50,
          marketingCategories: opts.marketingCategories ?? ['nda', 'bla', 'nda_ag'],
          page: 1,
          page_size: 50,
          ...(opts.drug_filter ? { drug_filter: opts.drug_filter } : {}),
          ...(opts.section_filter?.length ? { section_filter: opts.section_filter } : {}),
          ...(opts.regions?.length ? { regions: opts.regions } : (opts.region ? { region: opts.region } : {})),
          ...(opts.corpus === 'ha_reviews'
            ? { corpus: 'ha_reviews' as const, ...(opts.review_doc_types?.length ? { review_doc_types: opts.review_doc_types } : {}) }
            : {}),
          ...(opts.corpus !== 'ha_reviews' && opts.include_label_negotiations ? { include_label_negotiations: true } : {}),
          ...(opts.draft_comment_scope && opts.draft_comment_scope !== 'all' ? { draft_comment_scope: opts.draft_comment_scope } : {}),
          signal: controller.signal,
        })) as DrugAggregatedResponseV2 | null;
        // A new search may have aborted this one while the GET was in flight.
        if (controller.signal.aborted || !recovered) return false;
        setData(recovered);
        setDrugCount(recovered.drug_results?.length ?? 0);
        setError(null);
        return true;
      } catch (fallbackErr) {
        if (!(fallbackErr instanceof Error && fallbackErr.name === 'AbortError')) {
          console.error('[SSE] Fallback fetch failed:', fallbackErr);
        }
        return false;
      }
    };

    try {
      // Get Firebase auth token
      const currentUser = auth.currentUser;
      const token = currentUser ? await currentUser.getIdToken(false) : null;

      const opts = optionsRef.current;
      const body: Record<string, unknown> = {
        query: searchQuery,
        top_k: 50,
        marketing_categories: opts.marketingCategories ?? DEFAULT_MARKETING_CATEGORIES,
        page: 1,
        page_size: 50,
      };
      if (opts.regions?.length) {
        body['regions'] = opts.regions;
      } else if (opts.region) {
        body['region'] = opts.region;
      }
      if (opts.drug_filter) body['drug_filter'] = opts.drug_filter;
      if (opts.section_filter?.length) body['section_filter'] = opts.section_filter;
      if (opts.corpus === 'ha_reviews') {
        body['corpus'] = 'ha_reviews';
        if (opts.review_doc_types?.length) body['review_doc_types'] = opts.review_doc_types;
      } else if (opts.include_label_negotiations) {
        body['include_label_negotiations'] = true;
      }
      if (opts.draft_comment_scope && opts.draft_comment_scope !== 'all') {
        body['draft_comment_scope'] = opts.draft_comment_scope;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      Object.assign(headers, getImpersonationHeaders());

      const response = await fetch(
        `${STREAMING_API_URL}/v2/search/drugs/stream`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.status} ${response.statusText}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Accumulated state across events
      let accumulatedDrugs: DrugResultV2[] = [];
      let intent: IntentResponseV2 | null = null;
      let metadata: SearchMetadataV2 | null = null;
      // Positive completion signal: set only when the terminal `done` event
      // arrives. If the stream closes without it, the result is untrustworthy
      // and we recover via the persisted endpoint (see recoverViaFallback).
      let receivedDone = false;
      // Set when the backend/BFF emits an `error` event (e.g. the hardened
      // proxy signalling a mid-stream cut). Captured rather than thrown so a
      // single bad event doesn't bypass the recovery path below.
      let streamError: Error | null = null;

      // Process a list of parsed SSE events, updating accumulated state and React state
      const processEvents = (events: SSEEvent[]) => {
        for (const event of events) {
          try {
            const parsed = JSON.parse(event.data);

            switch (event.type) {
              case 'intent':
                intent = parsed as IntentResponseV2;
                setData(prev => ({
                  drug_results: prev?.drug_results ?? [],
                  evidence_chunks: prev?.evidence_chunks ?? [],
                  intent: parsed,
                  summary: null,
                  metadata: prev?.metadata ?? { total_candidates: 0, reranked_count: 0, total_drugs: 0, page: 1, page_size: 50, latency_ms: 0 },
                }));
                break;

              case 'drugs': {
                const newDrugs = parsed as DrugResultV2[];
                accumulatedDrugs = [...accumulatedDrugs, ...newDrugs];
                // Sort by score descending for display
                const sorted = [...accumulatedDrugs].sort((a, b) => b.score - a.score);
                setDrugCount(sorted.length);
                setData(prev => ({
                  drug_results: sorted,
                  evidence_chunks: prev?.evidence_chunks ?? [],
                  intent: prev?.intent ?? intent ?? { intent_type: 'LIST', concepts: [], preferred_sections: [], drug_filter: null, semantic_query: '', semantic_queries: [], filter_terms: [], ranking_terms: [], exclude_terms: [], is_reasoning: false },
                  summary: null,
                  metadata: prev?.metadata ?? { total_candidates: 0, reranked_count: 0, total_drugs: sorted.length, page: 1, page_size: 50, latency_ms: 0 },
                }));
                break;
              }

              case 'draft_results':
                // Must initialize full state when prev is null: a query with
                // zero label-drug matches never emits a 'drugs' event, so
                // draft_results may be the first (and only) state-bearing event.
                setData(prev => ({
                  drug_results: prev?.drug_results ?? [],
                  evidence_chunks: prev?.evidence_chunks ?? [],
                  intent: prev?.intent ?? intent ?? { intent_type: 'LIST', concepts: [], preferred_sections: [], drug_filter: null, semantic_query: '', semantic_queries: [], filter_terms: [], ranking_terms: [], exclude_terms: [], is_reasoning: false },
                  summary: null,
                  metadata: prev?.metadata ?? {
                    total_candidates: 0, reranked_count: 0, total_drugs: 0,
                    page: 1, page_size: 50, latency_ms: 0,
                  },
                  draft_results: parsed as DraftResultV2[],
                }));
                break;

              case 'done': {
                receivedDone = true;
                // Final event: contains full sorted drug_results and metadata.
                metadata = parsed.metadata as SearchMetadataV2;
                const finalDrugs = (parsed.drug_results as DrugResultV2[]) ?? accumulatedDrugs;
                setData(prev => ({
                  drug_results: finalDrugs,
                  evidence_chunks: prev?.evidence_chunks ?? [],
                  intent: prev?.intent ?? intent ?? { intent_type: 'LIST', concepts: [], preferred_sections: [], drug_filter: null, semantic_query: '', semantic_queries: [], filter_terms: [], ranking_terms: [], exclude_terms: [], is_reasoning: false },
                  summary: null,
                  metadata: {
                    total_candidates: metadata?.total_candidates ?? 0,
                    reranked_count: metadata?.reranked_count ?? 0,
                    // Prefer the backend's total (class-only enumeration returns a
                    // 50-drug page but a 941-drug cohort total); fall back for RAG.
                    total_drugs: metadata?.total_drugs ?? finalDrugs.length,
                    page: 1,
                    page_size: 50,
                    latency_ms: metadata?.latency_ms ?? 0,
                    // Pass through so the enumeration banner + diagnostic notice
                    // render. Conditional-spread so the optional fields are omitted
                    // rather than explicitly `undefined` — required by tsconfig
                    // `exactOptionalPropertyTypes: true`.
                    ...(metadata?.result_mode !== undefined && { result_mode: metadata.result_mode }),
                    ...(metadata?.notice !== undefined && { notice: metadata.notice }),
                    ...(metadata?.cache_key !== undefined && { cache_key: metadata.cache_key }),
                  },
                  // Preserve draft_results received before done.
                  draft_results: prev?.draft_results ?? [],
                }));
                setIsLoading(false);
                break;
              }

              case 'error':
                // Capture (don't throw) so the recovery path runs uniformly.
                streamError = new Error(parsed.message || 'Stream error');
                break;

              // prefilter_done, rerank_done, retrieval — informational, no UI update needed
              default:
                break;
            }
          } catch (parseErr) {
            // Skip unparseable events
            console.warn('[SSE] Failed to parse event:', event.type, parseErr);
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = parseSSEBuffer(buffer);
        buffer = remaining;
        processEvents(events);
        // An `error` event means the stream won't deliver results — stop here.
        if (streamError) break;
      }

      // Flush remaining buffer — the last event may not have a trailing \n\n
      // if the connection closed before it was fully sent
      if (!streamError && buffer.trim()) {
        const { events } = parseSSEBuffer(buffer + '\n\n');
        processEvents(events);
      }

      if (receivedDone) {
        setIsLoading(false);
      } else {
        // The stream either signalled an `error` event or closed without a
        // terminal `done` — both mean the live result is untrustworthy. The
        // backend persists results before completing (that's why history is
        // reliable), so recover them from the non-streaming endpoint instead
        // of rendering a false "No results found". Only surface an error if
        // recovery also fails.
        const recovered = await recoverViaFallback();
        if (!recovered && !controller.signal.aborted) {
          // Recovery failed too. Surface a real error (the `error` event's
          // message if we got one, else a generic cut message) rather than
          // leaving the user on the silent empty state this fix exists to fix.
          setError(streamError ?? new Error('Search stream ended before results were delivered'));
        }
        setIsLoading(false);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        // User navigated away or new search started — not an error
        return;
      }
      console.error('[SSE] Stream error:', err);
      // Stream failed mid-flight (BFF `error` event, non-OK response, or a
      // transport error). Try the persisted results before surfacing an error
      // so a transient cut doesn't read to the user as "no results".
      const recovered = await recoverViaFallback();
      if (!recovered && !controller.signal.aborted) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !query.trim()) {
      setData(null);
      setIsLoading(false);
      setError(null);
      setDrugCount(0);
      return;
    }

    startStream(query);

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    requestKey,
    enabled,
    startStream,
  ]);

  return { data, isLoading, error, drugCount };
}

export default useStreamingSearch;
